import { ReactionButton } from 'components/Comments/Comment/ReactionButton/ReactionButton'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { groupBy } from 'lodash'
import { observer } from 'mobx-react-lite'
import { forwardRef, PropsWithChildren, useMemo, useRef } from 'react'
import { BaseEmoji, CommentReaction } from 'types'
import Styles from './Reactions.module.scss'
import { ReactComponent as AddEmojiIcon } from 'assets/icons/addEmoji16.svg'
import { ReactionsProps } from './Types'
import Picker from '@emoji-mart/react'
import { useButton } from '@react-aria/button'
import { useHover } from '@react-aria/interactions'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useObjectRef } from 'utils/useObjectRef'

export const sortEmojiGroups = (a: CommentReaction[], b: CommentReaction[]) => {
  // Sort by emoji itself to remain in stable order
  return a[0].reaction < b[0].reaction ? -1 : 1
}

interface AddReactionButtonProps {
  onSelect?: (emoji: BaseEmoji) => void
}

const BaseButton = forwardRef<HTMLButtonElement, PropsWithChildren<{ className?: string }>>(
  ({ children, className, ...props }, fRef) => {
    const ref = useObjectRef(fRef)
    const { buttonProps } = useButton(props, ref)

    return (
      <button {...buttonProps} className={className} ref={ref}>
        {children}
      </button>
    )
  },
)

const AddReactionButton = (props: AddReactionButtonProps) => {
  return (
    <DropdownTrigger popoverClassName={Styles.popoverOverride}>
      <Tooltip placement="top" label="Add reaction">
        <div>
          <BaseButton className={Styles.addReaction}>
            <AddEmojiIcon />
          </BaseButton>
        </div>
      </Tooltip>
      {(close) => (
        <Picker
          autoFocus
          theme="light"
          previewPosition="none"
          onEmojiSelect={async (emoji: BaseEmoji) => {
            props.onSelect?.(emoji)
            close()
          }}
        />
      )}
    </DropdownTrigger>
  )
}

const Reactions = observer(({ reactions, onToggleReaction, isDisabled = false }: ReactionsProps) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const { isHovered, hoverProps } = useHover({})
  const groupedReactions = useMemo(() => {
    return groupBy(reactions, 'reaction')
  }, [reactions])

  if (reactions.length === 0) {
    return null
  }

  return (
    <div className={Styles.reactions} ref={ref} {...hoverProps}>
      {Object.keys(groupedReactions)
        .sort((a, b) => sortEmojiGroups(groupedReactions[a], groupedReactions[b]))
        .map((reaction) => (
          <ReactionButton
            key={reaction}
            emoji={reaction}
            reactions={groupedReactions[reaction]}
            onClick={() => {
              if (!isDisabled) onToggleReaction(reaction)
            }}
          />
        ))}

      {isHovered && !isDisabled && (
        <AddReactionButton
          onSelect={(emoji: BaseEmoji) => {
            if (!isDisabled) onToggleReaction(emoji.native)
          }}
        />
      )}
    </div>
  )
})

export { Reactions }
